import React, { useEffect, useState } from "react"
import { $moment, httpClient } from "funcs"
import Popup from "../../components/Popup"
import { notifyAboutError } from "../../methods/notifyUser"
import { signOut } from "../../methods/loadSelf"
import "pages/Styles/UIKit_v2.sass"
import "pages/Styles/Cells.sass"

export interface SessionItem {
  index: number
  last_used: string | null
  created_at: string | null
  device_info: { browser?: string; os?: string; ip_address?: string }
  is_current: boolean
}

interface SessionsPopupProps {
  onClose: () => void
  props: any // redux props for signOut / notify
}

function formatDate(value: string | null): string {
  if (!value) return "—"
  try {
    // Сервер хранит время в UTC; отображаем в часовом поясе браузера пользователя.
    const m = $moment.utc(value).local()
    if (!m.isValid()) return value
    return m.format("DD.MM.YYYY, HH:mm")
  } catch {
    return value
  }
}

function sessionLabel(session: SessionItem): string {
  const d = session.device_info || {}
  const parts = [d.browser, d.os].filter(Boolean)
  if (parts.length) return parts.join(", ")
  return `Сессия ${session.index + 1}`
}

export default function SessionsPopup({ onClose, props: reduxProps }: SessionsPopupProps) {
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [revokingIndex, setRevokingIndex] = useState<number | null>(null)
  const [revokingAll, setRevokingAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    httpClient
      .get("/user/sessions")
      .then((res) => {
        if (!cancelled) setSessions(res.data?.sessions || [])
      })
      .catch((err) => {
        if (!cancelled) notifyAboutError(reduxProps, err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  async function revokeOne(index: number) {
    setRevokingIndex(index)
    try {
      const { data } = await httpClient.post("/user/revoke_session", { index })
      if (data?.current_session_revoked) {
        onClose()
        signOut(reduxProps)
        return
      }
      const res = await httpClient.get("/user/sessions")
      setSessions(res.data?.sessions || [])
    } catch (err) {
      notifyAboutError(reduxProps, err)
    } finally {
      setRevokingIndex(null)
    }
  }

  async function revokeAll() {
    setRevokingAll(true)
    try {
      await httpClient.post("/user/revoke_all_sessions")
      onClose()
      signOut(reduxProps)
    } catch (err) {
      notifyAboutError(reduxProps, err)
      setRevokingAll(false)
    }
  }

  return (
    <Popup onClose={onClose}>
      <div className="sessions-popup-v2 ui-kit-v2">
        <h2 className="sessions-popup-v2__title Subheader_18">
          Управление сессиями
        </h2>
        <p className="sessions-popup-v2__description Body_16_v2_secondary">
          Здесь отображаются устройства, на которых вы вошли в аккаунт. Завершите сессию, если устройством больше не пользуетесь.
        </p>
        {loading ? (
          <div className="sessions-popup-v2__state flex-center Body_16_v2_secondary">
            Загрузка…
          </div>
        ) : sessions.length === 0 ? (
          <div className="sessions-popup-v2__state flex-center Body_16_v2_secondary">
            Нет активных сессий.
          </div>
        ) : (
          <>
            <ul className="cells-list sessions-popup-v2__list">
              {sessions.map((session) => (
                <li key={session.index} className="cell-card ui-kit-v2">
                  <div className="cell-card-header-row">
                    <div className="cell-card-header-title">
                      <h3 className="cell-card-title">{sessionLabel(session)}</h3>
                      <p className="cell-card-meta">
                        {formatDate(session.last_used || session.created_at)}
                      </p>
                    </div>
                    <div className="cell-card-header-actions">
                      {session.is_current && (
                        <span className="cell-card-tag">Текущая</span>
                      )}
                      <button
                        type="button"
                        className="new ui-kit-v2-button action-button secondary"
                        disabled={revokingIndex !== null || revokingAll}
                        onClick={() => revokeOne(session.index)}
                        style={{ flexShrink: 0 }}
                      >
                        <span className="button-text">
                          {revokingIndex === session.index ? "…" : "Завершить"}
                        </span>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="sessions-popup-v2__actions buttons-container-v2">
              <button
                type="button"
                className="new ui-kit-v2-button action-button secondary"
                disabled={revokingIndex !== null || revokingAll}
                onClick={revokeAll}
              >
                <span className="button-text">
                  {revokingAll ? "…" : "Завершить все сессии"}
                </span>
              </button>
            </div>
            <p className="sessions-popup-v2__note Body_16_v2_secondary">
              После завершения всех сессий потребуется повторный вход по номеру телефона.
            </p>
          </>
        )}
      </div>
    </Popup>
  )
}
